import React, { useCallback, useState, useRef } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { FaCheck, FaTrash, FaTimes } from "react-icons/fa";
import { AiOutlineUpload } from "react-icons/ai";
import _ from "lodash";

import { gateway } from "../../../../../../utils/api";
import {
  Container,
  DropZone,
  FileList,
  UploadProgress,
  WarningMessage,
  UploadInfoNotice,
  UploadQueueItem,
  UploadQueueList,
} from "./styles";

const MAX_CONCURRENT_UPLOADS = 2;
const MAX_FILES = 5;
// O arquivo passa pelo gateway e é gravado no Mongo (GridFS) pelo media-node
const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB por arquivo
const FILE_TOO_LARGE_MESSAGE =
  "Cada arquivo deve ter no máximo 1 GB. Reduza o tamanho ou envie um link em nuvem no comentário, se permitido.";
// Mensagem que o interceptor do gateway reconhece como cancelamento (não reporta ao Bugsnag)
const CANCEL_MESSAGE = "Solicitação cancelada";

const statusLabels = {
  pending: "Na fila",
  preparing: "Preparando...",
  uploading: "Enviando...",
  finalizing: "Finalizando...",
  done: "Concluído",
  error: "Erro",
  cancelled: "Cancelado",
};

function Upload({
  index,
  onAnswerChange,
  onDisableActions,
  onUserComment,
  userComment,
}) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const queueRef = useRef([]);

  const updateQueueItem = useCallback((id, updates) => {
    setUploadQueue((prev) => {
      const next = prev.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      );
      queueRef.current = next;
      return next;
    });
  }, []);

  const processOneUpload = useCallback(
    async (item) => {
      const { id, file } = item;
      const abortController = new AbortController();
      const cancelSource = axios.CancelToken.source();
      abortController.signal.addEventListener("abort", () =>
        cancelSource.cancel(CANCEL_MESSAGE)
      );
      updateQueueItem(id, {
        status: "uploading",
        progress: 0,
        abortController,
      });

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("mediaType", "ASSESSMENT_RESPONSE");

        const response = await gateway.post("/media/media/upload", formData, {
          cancelToken: cancelSource.token,
          onUploadProgress: (e) => {
            if (!e.lengthComputable) return;
            const progress = Math.round((e.loaded / e.total) * 100);
            // 100% do envio ainda depende do media-node gravar no GridFS
            if (progress >= 100) {
              updateQueueItem(id, { status: "finalizing", progress: 95 });
            } else {
              updateQueueItem(id, { progress });
            }
          },
        });

        const fileCode = _.get(
          response,
          "ox_standard.ox_identification.cr_code",
          null
        );
        updateQueueItem(id, {
          status: fileCode ? "done" : "error",
          progress: fileCode ? 100 : 0,
          error: fileCode ? null : "Resposta inválida do servidor",
          result: fileCode ? { name: file.name, code: fileCode } : null,
        });
      } catch (err) {
        if (axios.isCancel(err) || abortController.signal.aborted) {
          updateQueueItem(id, { status: "cancelled" });
          return;
        }
        updateQueueItem(id, {
          status: "error",
          error:
            (err && err.response && err.response.data && err.response.data.message) ||
            (err && err.message) ||
            "Falha no upload",
        });
      }
    },
    [updateQueueItem]
  );

  const runQueue = useCallback(() => {
    const pending = queueRef.current.filter((i) => i.status === "pending");
    const running = queueRef.current.filter((i) =>
      ["preparing", "uploading", "finalizing"].includes(i.status)
    );
    if (running.length >= MAX_CONCURRENT_UPLOADS || pending.length === 0)
      return;
    const toStart = pending.slice(
      0,
      MAX_CONCURRENT_UPLOADS - running.length
    );
    toStart.forEach((item) => processOneUpload(item));
  }, [processOneUpload]);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections = []) => {
      const hasTooLarge = fileRejections.some((rej) =>
        (rej.errors || []).some((e) => e.code === "file-too-large")
      );
      if (hasTooLarge) {
        setUploadError(FILE_TOO_LARGE_MESSAGE);
        if (!acceptedFiles || acceptedFiles.length === 0) return;
      }

      const withinLimit = (acceptedFiles || []).filter(
        (f) => f.size <= MAX_FILE_SIZE_BYTES
      );
      if (withinLimit.length === 0) return;

      const total =
        uploadedFiles.length + uploadQueue.length + withinLimit.length;
      if (total > MAX_FILES) {
        setUploadError("Limite de 5 arquivos atingido.");
        return;
      }
      if (!hasTooLarge) {
        setUploadError(null);
      }
      const newItems = withinLimit.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        progress: 0,
        status: "pending",
        error: null,
        result: null,
        abortController: null,
      }));
      const next = [...uploadQueue, ...newItems];
      queueRef.current = next;
      setUploadQueue(next);
      setTimeout(runQueue, 0);
      onDisableActions(true);
    },
    [uploadedFiles.length, uploadQueue, onDisableActions, runQueue]
  );

  React.useEffect(() => {
    runQueue();
  }, [uploadQueue, runQueue]);

  React.useEffect(() => {
    const queue = queueRef.current;
    const done = queue.filter((i) => i.status === "done" && i.result);
    const stillWorking = queue.some((i) =>
      ["pending", "preparing", "uploading", "finalizing"].includes(i.status)
    );
    if (done.length > 0) {
      const newUploaded = done.map((i) => i.result);
      setUploadedFiles((prev) => {
        const combined = [...prev, ...newUploaded].slice(0, MAX_FILES);
        onAnswerChange(index, combined.map((f) => f.code));
        return combined;
      });
      setUploadQueue((prev) => {
        const next = prev.filter((i) => i.status !== "done" || !i.result);
        queueRef.current = next;
        return next;
      });
      setTimeout(runQueue, 0);
    }
    if (!stillWorking && queue.length === 0) {
      onDisableActions(false);
    }
  }, [uploadQueue, index, onAnswerChange, onDisableActions, runQueue]);

  const cancelUpload = useCallback(
    (id) => {
      const item = queueRef.current.find((i) => i.id === id);
      if (item && item.abortController) {
        item.abortController.abort();
      }
      updateQueueItem(id, { status: "cancelled" });
      setTimeout(runQueue, 50);
    },
    [updateQueueItem, runQueue]
  );

  const removeFromQueue = useCallback((id) => {
    setUploadQueue((prev) => {
      const next = prev.filter((i) => i.id !== id);
      queueRef.current = next;
      const stillWorking = next.some((i) =>
        ["pending", "preparing", "uploading", "finalizing"].includes(i.status)
      );
      if (!stillWorking && next.length === 0) onDisableActions(false);
      return next;
    });
  }, [onDisableActions]);

  const removeFile = useCallback(
    (fileCode) => {
      const updated = uploadedFiles.filter((f) => f.code !== fileCode);
      setUploadedFiles(updated);
      onAnswerChange(index, updated.map((f) => f.code));
    },
    [uploadedFiles, index, onAnswerChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_SIZE_BYTES,
    disabled: uploadQueue.some((i) =>
      ["preparing", "uploading", "finalizing"].includes(i.status)
    ),
    multiple: true,
    maxFiles: MAX_FILES - uploadedFiles.length - uploadQueue.length,
    preventDropOnChildren: true,
    noClick: false,
    noKeyboard: false,
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Container>
      <UploadInfoNotice role="status">
        Envio de arquivos para esta avaliação: tamanho máximo de <strong>1 GB</strong>{" "}
        por arquivo e até <strong>5 arquivos</strong> no total. Arquivos maiores que o
        limite não serão aceitos.
      </UploadInfoNotice>
      <DropZone {...getRootProps({ refKey: "innerRef" })}>
        <input {...getInputProps()} />
        <p id="text-upload">
          Arraste e solte ou clique aqui para selecionar seus arquivos.
          <AiOutlineUpload />
        </p>
      </DropZone>

      {uploadQueue.length > 0 && (
        <UploadQueueList>
          {uploadQueue.map((item) => (
            <UploadQueueItem key={item.id} status={item.status}>
              <div className="file-info">
                <span className="file-name" title={item.file.name}>
                  {item.file.name}
                </span>
                <span className="file-size">
                  {formatFileSize(item.file.size)}
                </span>
              </div>
              <div className="progress-row">
                <UploadProgress>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                  <span>{item.progress}%</span>
                </UploadProgress>
              </div>
              <div className="status-row">
                <span className="status-label">
                  {statusLabels[item.status] || item.status}
                </span>
                {item.error && (
                  <span className="status-error">{item.error}</span>
                )}
                {["pending", "preparing", "uploading", "finalizing"].includes(
                  item.status
                ) && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => cancelUpload(item.id)}
                  >
                    <FaTimes /> Cancelar
                  </button>
                )}
                {["done", "error", "cancelled"].includes(item.status) && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeFromQueue(item.id)}
                  >
                    Remover
                  </button>
                )}
              </div>
            </UploadQueueItem>
          ))}
        </UploadQueueList>
      )}

      {uploadError && (
        <WarningMessage className="error">{uploadError}</WarningMessage>
      )}

      {uploadedFiles.length > 0 && (
        <FileList>
          {uploadedFiles.map((file) => (
            <div key={file.code} className="file-item">
              <FaCheck size={20} color="#1890ff" />
              <span>{file.name}</span>
              <FaTrash onClick={() => removeFile(file.code)} />
            </div>
          ))}
        </FileList>
      )}

      <div className="form-group w-100 mt-4">
        <label>Adicione um comentário</label>
        <textarea
          className="form-control"
          rows="4"
          value={userComment}
          disabled={uploadQueue.some((i) =>
            ["preparing", "uploading", "finalizing"].includes(i.status)
          )}
          onChange={(e) => onUserComment(e.target.value)}
        />
      </div>
    </Container>
  );
}

export default Upload;
